import type { ApiErrorBody } from '@wms/shared';
import { clearWarehouseSelection, getCurrentWarehouseId } from './warehouse';

export class ApiError extends Error {
  readonly code: string;
  readonly params: Record<string, unknown>;
  readonly status: number;

  constructor(body: ApiErrorBody, status: number) {
    super(body.error.code);
    this.code = body.error.code;
    this.params = body.error.params ?? {};
    this.status = status;
  }
}

const CSRF_COOKIE_NAME = 'wms_csrf';

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : undefined;
}

/**
 * 统一 API 客户端 —— 同源部署（架构书 §3.3），Cookie 自动随请求发送，
 * 双提交 CSRF token 从 cookie 读出回填到请求头（见后端 csrf.guard.ts）。
 */
export async function apiFetch<T>(
  path: string,
  options: {
    method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
    body?: unknown;
    headers?: Record<string, string>;
    /**
     * M3b MEDIUM-1：编辑器卸载/场景切换时触发的补救性保存请求用这个，让浏览器
     * 在发起请求的文档已经卸载之后仍然把它送达（`fetch` 的 keepalive 语义），
     * 而不是被当成一次普通的、页面还在时才会完成的请求直接丢弃。
     */
    keepalive?: boolean;
  } = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { ...options.headers };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  const csrf = readCookie(CSRF_COOKIE_NAME);
  if (csrf && method !== 'GET') headers['x-csrf-token'] = csrf;
  // 当前仓库上下文（Spec §5）——后端仍然是唯一的权威校验点（resolveAuthorizedWarehouse
  // fail-closed），这里只是把已选仓库带上，不代表信任前端的选择本身。
  const warehouseId = getCurrentWarehouseId();
  if (warehouseId && !headers['x-warehouse-id']) headers['x-warehouse-id'] = warehouseId;

  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'include',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    keepalive: options.keepalive,
  });

  if (res.status === 204) return undefined as T;

  const json = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const error = new ApiError(json as ApiErrorBody, res.status);
    // 【M2 独立复审 LOW】本地记住的仓库可能在后端被归档、或访问权已被收回
    // （M-4：归档仓库不再能通过 X-Warehouse-Id 解析出授权上下文）——继续沿用
    // 这个失效的 id 只会让后续每个请求都拿到同一个 403，页面上则表现成一棵
    // "看起来没有数据"的空树，用户分不清是真的没数据还是访问被拒绝。这里
    // 只要看到这个特定错误码就清掉本地选择，交给 <RequireWarehouse> 已有的
    // F1 流程重新走一遍选择页，不在每个调用方各自处理。
    if (error.code === 'AUTHZ.WAREHOUSE_ACCESS_DENIED' && warehouseId) {
      clearWarehouseSelection();
    }
    throw error;
  }
  return json as T;
}
